// @spec BCP-STR-029
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useExerciseFindings,
  useFindingsReport,
  useLinkTask,
  useUpdateFindingStatus,
  useVerifyFinding,
} from '@/features/bcp/api/exercise-findings';
import { ExerciseFindingsTable } from '@/features/bcp/components/ExerciseFindingsTable';
import { FindingStatusUpdateDialog } from '@/features/bcp/components/FindingStatusUpdateDialog';
import { FindingVerifyDialog } from '@/features/bcp/components/FindingVerifyDialog';
import { FindingsFilters } from '@/features/bcp/components/FindingsFilters';
import { FindingsResolutionReport } from '@/features/bcp/components/FindingsResolutionReport';
import type {
  ExerciseFindingResponse,
  FindingsFiltersState,
} from '@/features/bcp/types/exercise-findings';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/bcp/findings')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.exerciseFindings'),
  }),
  component: FindingsPage,
});

function FindingsPage() {
  const { t } = useTranslation('bcp');
  const { user } = useUser();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FindingsFiltersState>({
    exerciseId: '',
    severity: '',
    status: '',
    ownerId: '',
    search: '',
    overdueOnly: false,
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [reportOpen, setReportOpen] = useState(false);

  // Dialog state
  const [statusDialogFinding, setStatusDialogFinding] = useState<ExerciseFindingResponse | null>(
    null,
  );
  const [verifyDialogFinding, setVerifyDialogFinding] = useState<ExerciseFindingResponse | null>(
    null,
  );

  // Build query params
  const queryParams: Record<string, string | number | boolean | undefined> = {
    page,
    pageSize: 20,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  };
  if (filters.exerciseId) queryParams.exerciseId = filters.exerciseId;
  if (filters.severity) queryParams.severity = filters.severity;
  if (filters.status) queryParams.status = filters.status;
  if (filters.ownerId) queryParams.ownerId = filters.ownerId;
  if (filters.search) queryParams.search = filters.search;
  if (filters.overdueOnly) queryParams.overdueOnly = true;

  const {
    data: findingsData,
    isLoading,
    isError,
  } = useExerciseFindings(queryParams);
  const { data: reportData } = useFindingsReport({}, reportOpen);

  const updateStatusMutation = useUpdateFindingStatus();
  const verifyMutation = useVerifyFinding();
  const linkTaskMutation = useLinkTask();

  const handleFilterChange = (newFilters: FindingsFiltersState) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleStatusUpdate = (status: 'in_progress' | 'resolved', resolutionNotes?: string) => {
    if (!statusDialogFinding) return;
    updateStatusMutation.mutate(
      {
        findingId: statusDialogFinding.id,
        data: { status, resolutionNotes },
      },
      {
        onSuccess: () => {
          toast.success(t('bcp:toast.findingStatusUpdated'));
          setStatusDialogFinding(null);
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { detail?: { message?: string } } } }).response?.data
              ?.detail?.message ?? 'Failed to update status';
          toast.error(message);
        },
      },
    );
  };

  const handleVerify = (notes?: string) => {
    if (!verifyDialogFinding) return;
    verifyMutation.mutate(
      {
        findingId: verifyDialogFinding.id,
        data: { notes },
      },
      {
        onSuccess: () => {
          toast.success(t('bcp:toast.findingVerified'));
          setVerifyDialogFinding(null);
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { detail?: { message?: string } } } }).response?.data
              ?.detail?.message ?? 'Failed to verify finding';
          toast.error(message);
        },
      },
    );
  };

  const handleLinkTask = (finding: ExerciseFindingResponse) => {
    // In a real implementation, this would open a task picker dialog
    // For now, show a toast indicating the action
    void linkTaskMutation;
    toast.info(t('bcp:toast.linkTaskToTaskPickerNotYetImplemented', { finding: finding.title }));
  };

  const pagination = findingsData?.pagination;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Exercise Findings</h1>
          <p className="text-muted-foreground">
            Track and manage findings from BCP exercises
          </p>
        </div>

        {/* Summary stats */}
        {findingsData?.summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <StatCard label="Open" value={findingsData.summary.openCount} />
            <StatCard label="In Progress" value={findingsData.summary.inProgressCount} />
            <StatCard label="Resolved" value={findingsData.summary.resolvedCount} />
            <StatCard label="Verified" value={findingsData.summary.verifiedCount} />
            <StatCard label="Critical" value={findingsData.summary.criticalCount} color="red" />
            <StatCard label="High" value={findingsData.summary.highCount} color="orange" />
            <StatCard label="Overdue" value={findingsData.summary.overdueCount} color="red" />
          </div>
        )}

        {/* Filters */}
        <FindingsFilters filters={filters} onFilterChange={handleFilterChange} />

        {/* Table */}
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load findings. Please try again.
          </div>
        ) : (
          <>
            <ExerciseFindingsTable
              findings={findingsData?.items ?? []}
              onStatusUpdate={setStatusDialogFinding}
              onVerify={setVerifyDialogFinding}
              onLinkTask={handleLinkTask}
            />

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {(pagination.page - 1) * pagination.pageSize + 1}-
                  {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of{' '}
                  {pagination.totalItems} findings
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.min(pagination.totalPages, p + 1)); }}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Report section (collapsible) */}
        <Collapsible open={reportOpen} onOpenChange={setReportOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span>Resolution Report</span>
              {reportOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            {reportData ? (
              <FindingsResolutionReport report={reportData} />
            ) : (
              <div className="space-y-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Status Update Dialog */}
      {statusDialogFinding && (
        <FindingStatusUpdateDialog
          finding={statusDialogFinding}
          open={!!statusDialogFinding}
          onOpenChange={(open) => {
            if (!open) setStatusDialogFinding(null);
          }}
          onSubmit={handleStatusUpdate}
          isSubmitting={updateStatusMutation.isPending}
        />
      )}

      {/* Verify Dialog */}
      {verifyDialogFinding && (
        <FindingVerifyDialog
          finding={verifyDialogFinding}
          open={!!verifyDialogFinding}
          onOpenChange={(open) => {
            if (!open) setVerifyDialogFinding(null);
          }}
          onSubmit={handleVerify}
          isSubmitting={verifyMutation.isPending}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: 'red' | 'orange';
}) {
  return (
    <div className="rounded-md border p-3 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`text-xl font-bold ${
          color === 'red'
            ? 'text-red-600'
            : color === 'orange'
              ? 'text-orange-600'
              : ''
        }`}
      >
        {value}
      </p>
    </div>
  );
}
